import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg } from "@/lib/api";

const NONE = "__none";

const defaults = (fields) =>
  Object.fromEntries(fields.map((f) => [f.name, f.default ?? (f.type === "switch" ? true : f.type === "multicheck" ? [] : f.type === "select" && f.nullable ? null : "")]));

const FieldInput = ({ f, value, set, tid }) => {
  if (f.type === "textarea") return <Textarea data-testid={tid} value={value ?? ""} onChange={(e) => set(e.target.value)} rows={f.rows || 3} />;
  if (f.type === "switch") return <Switch data-testid={tid} checked={!!value} onCheckedChange={set} />;
  if (f.type === "select")
    return (
      <Select value={value ?? NONE} onValueChange={(v) => set(v === NONE ? null : v)}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
        <SelectContent>
          {f.nullable && <SelectItem value={NONE}>{f.noneLabel || "Cualquiera"}</SelectItem>}
          {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  if (f.type === "multicheck")
    return (
      <div className="flex flex-wrap gap-3" data-testid={tid}>
        {f.options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <Checkbox checked={(value || []).includes(o.value)} onCheckedChange={(c) => set(c ? [...(value || []), o.value] : (value || []).filter((x) => x !== o.value))} />
            {o.label}
          </label>
        ))}
      </div>
    );
  return (
    <Input data-testid={tid} type={f.type || "text"} value={value ?? ""} placeholder={f.placeholder}
      onChange={(e) => set(f.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} />
  );
};

export const FormDialog = ({ open, onOpenChange, title, fields, initial, onSubmit, testid }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...defaults(fields), ...(initial || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {fields.filter((f) => !f.hidden?.(form, initial)).map((f) => (
            <div key={f.name} className={f.type === "switch" ? "flex items-center justify-between" : "space-y-1.5"}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}{f.required && " *"}</Label>
              <FieldInput f={f} value={form[f.name]} set={(v) => setForm((s) => ({ ...s, [f.name]: v }))} tid={`${testid}-${f.name}`} />
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid={`${testid}-submit`}>{saving ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
